<template>
    <main id='slides'>
        <nav :class='navClass' id='slides-nav'>
            <svg class='nav-icon' viewBox='0 0 48 48' @click='onNavIconClick'>
                <g class='menu'>
                    <path d='M45,22H3c-0.552,0-1,0.448-1,1v2c0,0.552,0.448,1,1,1h42c0.552,0,1-0.448,1-1v-2 C46,22.448,45.552,22,45,22z'></path>
                    <path d='M45,8H3C2.448,8,2,8.448,2,9v2c0,0.552,0.448,1,1,1h42c0.552,0,1-0.448,1-1V9C46,8.448,45.552,8,45,8z'></path>
                    <path d='M45,36H25c-0.552,0-1,0.448-1,1v2c0,0.552,0.448,1,1,1h20c0.552,0,1-0.448,1-1v-2C46,36.448,45.552,36,45,36 z'></path>
                </g>
                <g class='close'>
                    <line x1='38' y1='10' x2='10' y2='38'></line>
                    <line x1='38' y1='38' x2='10' y2='10'></line>
                </g>
            </svg>
            <h2 v-show='ifActiveMenuClass'>Menu</h2>
            <ul ref='slidesNav' v-show='ifActiveMenuClass'>
                <li v-for='(id, index) in ids' :key='index'>
                    <a :href='id' :data-i='index + 1' @click='onNavLinkClick' class='nav-slide-name'>
                        <span :data-page='index + 1'>{{ navSlideName(id) }}</span>
                    </a>
                </li>
            </ul>
        </nav>
        <article ref='slides'>
           <slot></slot>
        </article>
    </main>
</template>

<script>

import { TweenMax, TimelineMax } from 'gsap';
import hammerjs from 'hammerjs';
import 'imports-loader?define=>false!gsap/ScrollToPlugin';

export default {
    name: 'Slides',
    props: {
        navPosition: {
            type: String,
            default: 'right'
        },
        scripts: {
            type: Function,
            default({ currentSlide, slideDuration }) {
                return { slideStart() {}, slideEnd() {} }
            }
        },
        slideDuration: {
            type: Number,
            default: 1
        }
    },
    data() {
        return {
            ids: null,
            isAnimating: false,
            currentSlide: null,
            currentIndex: 0,
            activeClass: 'active',
            completeClass: 'complete',
            activeMenuClass: 'active-mobile-menu',
            ifActiveMenuClass: false,
            keyCodes: { UP: 38, DOWN: 40 },
            slideEase: Sine.easeInOut,
            isMobile: false
        };
    },
    computed: {
        slides() {
            return Array.from(this.slidesContainer.children);
        },
        navClass() {
            return 'position-' + this.navPosition;
        },
        navLinks() {
            return Array.from(this.$refs.slidesNav.children);
        }
    },

    // Called when the the component is initialized, once
    mounted() {
        
        this.slidesContainer    = this.$refs.slides;
        this.slideGoPrev        = Array.from(this.$el.querySelectorAll('.go-prev'));
        this.slideGoNext        = Array.from(this.$el.querySelectorAll('.go-next'));
        this.ids                = this.slides.map(this.createIds); 
        this.pageHeight         = window.innerHeight;

        // Get the current slide by checking local storage and comparing it to the slide length
        const storedId          = parseInt(window.localStorage.getItem('_slideID')) - 1 || 0;
        const initialId         = storedId > this.slides.length ? this.slides.length - 1 : storedId;
        this.currentSlide       = this.slides[initialId];
        
        // Invoke the rest of the setup functions and go to the current slide
        this.addSlideIndex().addEvents().ifMobile().goToSlide(this.currentSlide);
    },
    methods: {
        addEvents() {

            // Add keyboard events
            document.addEventListener('keydown', this.onKeyDown);

            // Add window resize events
            window.addEventListener('resize', this.debounce(() => this.onResize(), 250));

            // Add mouse wheel events
            window.addEventListener('mousewheel', this.throttle(event => this.onMouseWheel(event), 10));
            window.addEventListener('DOMMouseScroll', this.throttle(event => this.onMouseWheel(event), 10)); // FF

            // Add click events for buttons
            this.slideGoPrev.forEach(button => button.addEventListener('click', this.goToPrevSlide));
            this.slideGoNext.forEach(button => button.addEventListener('click', this.goToNextSlide));

            // Add touch events for devices
            const container = new Hammer(this.slidesContainer);
            container.get('pan').set({ direction: Hammer.DIRECTION_ALL });
            container.on('panleft panright panup pandown tap press', event => {
                if (event.type === 'panup') { this.goToNextSlide(); }
                if (event.type === 'pandown') { this.goToPrevSlide(); }
            });

            return this;
        },

        // Add an index counter to each slide
        addSlideIndex() {
            this.slides.forEach((slide, index) => slide.setAttribute('data-i', index + 1));
            return this;
        },

        onKeyDown(event) {
            const keyPress = event.keyCode;
            if (keyPress === this.keyCodes.UP) {
                this.goToPrevSlide();
            }
            else if (keyPress === this.keyCodes.DOWN) {
                this.goToNextSlide();
            }
        },

        onMouseWheel(event) {
           
            // Normalize event wheel delta
            const delta = event.wheelDelta / 30 || -event.detail;

            // If the user scrolled up, it goes to previous slide, otherwise - to next slide
            if      (delta < -1) this.goToNextSlide();
            else if (delta >  1) this.goToPrevSlide();
        },

        goToPrevSlide() {
            if (this.currentSlide.previousElementSibling) {
                this.goToSlide(this.currentSlide.previousElementSibling);
            }
        },

        goToNextSlide() {
            if (this.currentSlide.nextElementSibling) {
                this.goToSlide(this.currentSlide.nextElementSibling);
            }
        },

        goToSlide(slide, duration) {

            // Temporary fix for zero slide duration
            duration = duration === undefined ? this.slideDuration : duration;

            // If the slides are not animating and there's a slide
            if (!this.isAnimating && slide) {

                this.isAnimating = true;
                this.ifActiveMenuClass = false;
                this.currentSlide = slide;
                this.currentIndex = parseInt(this.currentSlide.dataset.i) - 1 || 0;
                
                // Set the slide name to the local storage for remembering the last slide position
                // on a window refresh
                window.localStorage.setItem('_slideID', this.currentSlide.dataset.i);

                // Sliding to current slide, with callbacks
                TweenMax.to(this.slidesContainer, duration, {
                    onStart:            this.onSlideStart,
                    onStartParams:      [ this.currentIndex, this.currentSlide, this.slideDuration ],
                    onComplete:         this.onSlideEnd,
                    onCompleteParams:   [ this.currentIndex, this.currentSlide ],
                    scrollTo:           { y: this.pageHeight * this.currentIndex },
                    ease:               this.slideEase,
                });
            }

            return this;
        },

        onSlideStart(currentIndex, currentSlide, slideDuration) {

            // Update the active class for the slides and nav
            this.updateSlideClass(this.activeClass, currentIndex);

            // Initialize the aniamtions and pass in the current slide and duration
            // And assign the function reference to "slideScripts", from the scripts.js file
            this.slideScripts = this.scripts({ currentSlide, slideDuration });
            this.slideScripts.slideStart();

            this.$emit('slideStart', {
                slideName:      currentSlide.id,
                domElement:     currentSlide,
                currentIndex:   currentIndex,
            });
        },

        onSlideEnd(currentIndex, currentSlide) {

            // When the slide finishes moving, add the "complete" css class to the slides and nav
            this.updateSlideClass(this.completeClass, currentIndex);

            // Call the scriptsEnd() function from the scripts.js file
            this.slideScripts.slideEnd();

            // Set the animating flag to flase
            this.isAnimating = false;

            // Update the window hash url
            window.location.hash = '#' + this.currentSlide.id;

            // Add an attribute on the root to specify the slide selected
            document.querySelector('#slides').setAttribute('data-current', currentSlide.id);

            // Only resize the window if on Mobile Device, has a glitch
            if (this.isMobile) this.resize();

            this.$emit('slideEnd', {
                slideName:      currentSlide.id,
                domElement:     currentSlide,
                currentIndex:   currentIndex,
            });
        },

        onResize() {
            const newPageHeight = window.innerHeight;
            if (this.pageHeight !== newPageHeight) {
                this.pageHeight = newPageHeight;
                this.resize();
            }
        },

        resize() {
            TweenMax.set([this.slidesContainer, this.slides], {
                height: this.pageHeight + 'px'
            });
            TweenMax.set(this.slidesContainer, {
                scrollTo: { y: this.pageHeight * this.currentIndex }
            });
        },

        // Remove the "#" and "-" characters for navigation links
        navSlideName(id) {
            return id.replace(/#/gmi, '').replace(/-/gmi, ' ');
        },

        // Create IDs for creation of the navigation links
        createIds(slide, index) {

            // If there is an ID on the section use the explicit ID, otherwise use the index 
            return slide.id ? ('#' + slide.id) : ('Slide ' + (index + 1).toString());
        },

        onNavIconClick() {
            this.ifActiveMenuClass = !this.ifActiveMenuClass ? true : false;
            this.$el.classList.toggle(this.activeMenuClass);
        },

        onNavGridClick() {
            this.$el.classList.contains(this.activeGridClass) ? this.destroyGrid() : this.createGrid();
        },

        createGrid() {
            var count     = this.slides.length;
            var cols      = Math.ceil(Math.sqrt(count));
            var rows      = Math.round(Math.sqrt(count));
            var cell      = 100/cols;
            var gap       = cell * 0.2/100;
            var scale     = (cell/100) - gap;
            var size      = cell - ((gap * 100) / 2);
            var x         = (gap * 100);
            var y         = (gap * 100);

            this.$el.classList.add(this.activeGridClass);
            this.slides.forEach(slide => {
                slide.style.height = this.pageHeight + 'px';
                slide.style.transform = `scale(${scale}) translate(${x}%, ${y}%)`;
            });
        },

        onNavLinkClick(event) {
            event.preventDefault();

            // Get the ID from the anchor link and goToSlide()
            const id = event.target.dataset.i - 1;
            const slide = this.slides[id];
            this.goToSlide(slide);

            // Remove the active class to close the mobile menu and reset everything
            this.$el.classList.remove(this.activeMenuClass);
        },

        updateSlideClass(className, slideIndex) {

            // Remove the class from all the links
            [this.navLinks, this.slides].forEach(removeClass);

            // Then add the class to the active link
            [this.navLinks, this.slides].forEach(addClass);

            function removeClass(set) {
                set.forEach(item => item.classList.remove(className));
            }
            function addClass(set) {
                set[slideIndex].classList.add(className);
            }
        },

        // debounce() taken from Underscore.js
        debounce(func, wait, immediate) {
            let timeout;
            return function() {
                let args = arguments;
                let later = () => {
                    timeout = null;
                    if (!immediate) func.apply(this, args);
                };
                let callNow = immediate && !timeout;
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
                if (callNow) func.apply(this, args);
            };
        },

        throttle(func, wait) {
            let throttle;
            return function() {
                if (!throttle) {
                    func.apply(this, arguments);
                    throttle = true;
                    setTimeout(() => throttle = false, wait);
                }
            };
        },
        ifMobile() {
            if( navigator.userAgent.match(/Android/i) ||
                navigator.userAgent.match(/webOS/i) ||
                navigator.userAgent.match(/iPhone/i) ||
                navigator.userAgent.match(/iPod/i) ||
                navigator.userAgent.match(/iPad/i) ||
                navigator.userAgent.match(/BlackBerry/i) ||
                navigator.userAgent.match(/IEMobile/i) ||
                navigator.userAgent.match(/Opera Mini/i)
            ) {
                this.isMobile = true;
            }
            return this;
        }
    }
}
</script>

<style lang='scss'>
@import './styles/vars';

$color-primary: $color-blue;

body { margin: 0; }

#slides {
    width: 100vw;
    height: 100vh;
}

#slides article {
    position: relative;
    left: 0;
    top: 0;
    width: 100%;
    height: 100vh;
    overflow: hidden;
}

#slides section {
    position: relative;
    width: 100%;
    height: inherit;
    overflow: hidden;
}

#slides nav {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 9001;
    pointer-events: none;
    @media (max-width: 767px) {
        pointer-events: none;
        background: transparent;
        right: 0;
        left: 0;
        width: 100%;
    }
    .nav-icon {
        position: absolute;
        width: 2.5vw;
        top: 3vw;
        right: 3vw;
        pointer-events: all;
        cursor: pointer;
        transition: all 0.3s ease-in-out;
        @media (max-width: 767px) {
            width: 8vw;
            top: 6vw;
            right: 6vw;
        }
        line {
            stroke-width: 4;
            stroke-linecap: square;
            stroke-miterlimit: 10;
            stroke-linejoin: miter;
        }
    }
}

#slides nav.position-right {
    right: 0;
    text-align: right;
    li .nav-slide-name {
        right: 85%;
        @media (max-width: 767px) {
            right: initial;
        }
    }
    li .nav-slide-name {
        justify-content: flex-end;
    }
}

#slides nav.position-left {
    left: 0;
    .nav-slide-name {
        left: 85%;
        @media (max-width: 767px) {
            left: 0;
        }
    }
    li .nav-slide-name {
        justify-content: flex-start;
    }
}

#slides nav h2 {
    color: black;
    font-size: 15vw;
    position: absolute;
    left: 10%;
    width: 40%;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    opacity: 0.2;
}

#slides nav ul {
    width: 100vw;
    height: 100vh;
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: absolute;
    top: 0; right: 0; bottom: 0; left: 0;
    padding: 0 10%;
    @media (max-width: 767px) {
        height: 100%;
        background: $color-primary;
        display: flex;
        align-items: flex-end;
        flex-direction: column;
        justify-content: center;
        transform: translateX(100%);
        transition: transform 0.15s ease-in;
    }
}

/* Active Mobile Menu */
#slides.active-mobile-menu nav {
    pointer-events: all;
    background: $color-blue;
}

#slides.active-mobile-menu .nav-icon {
    z-index: 1;
    .menu { fill: transparent; }
    .close { fill: white; line { stroke: white } }
}

#slides.active-mobile-menu nav ul {
    transform: translateX(0);
    transition: transform 0.3s ease-out;
}

#slides nav ul li {
    display: block;
    margin: 0;
    display: flex;
    align-items: center;
    padding: 0;
    justify-content: center;
    text-transform: capitalize;
    position: relative;
    &.active::before {
        content: '';
        display: inline-block;
        left: 100%;
        background: $color-orange;
        border-radius: 100%;
        width: 1vw;
        height: 1vw;
        position: absolute;
        @media (max-width: 767px) {
            width: 2vw;
            height: 2vw;
        }
    }
}

#slides nav ul li .nav-slide-name {
    white-space: nowrap;
    display: flex;
    align-items: center;
    padding: 5px;
    transition: background 0.5s ease-in-out .3s;
    @media (max-width: 767px) {
        opacity: 1;
        position: relative;
        display: block;
        width: 100%;
    }
    span {
        display: block;
        pointer-events: none;
        font-weight: 600;
        @media (max-width: 767px) {
            font-size: 4vw;
            background: none;
        }
    }
    /* Page Number indicator */
    span::after {
        width: 30px;
        opacity: 0.5;
        text-align: right;
        content: ' ' attr(data-page);
        display: inline-block;
    }
}

#slides nav ul li:hover .nav-slide-name {
    background: rgba($color-gold, 1);
    transition: background 0.3s ease-out 0s;
    opacity: 1; 
}

#slides nav ul li.active:hover .nav-slide-name {
    background: transparent;
    opacity: 1; 
}

#slides nav ul li a {
    width: 100%;
    height: inherit;
    text-decoration: none;
    color: inherit;
}

</style>



// WEBPACK FOOTER //
// src/Slides.vue